import { useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/shared/lib/utils';
import { useAuthStore } from '@/shared/store/auth';
import { usePermissions } from '@/shared/hooks/usePermissions';
import type { Permission } from '@/shared/types/permissions';
import {
  InboxIcon,
  ChatIcon,
  TargetIcon,
  DigestIcon,
  FolderIcon,
  SettingsIcon,
  LogoIcon,
  CloseIcon,
  MegaphoneIcon,
  EditIcon,
  ClockIcon,
  UsersIcon,
} from '@/shared/components/icons';

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  permission?: Permission;
  end?: boolean;
}

const navItems: NavItem[] = [
  // Директолог (спека 0139) — первыми: это теперь главный экран.
  { to: '/audit', icon: MegaphoneIcon, label: 'Аудит' },
  { to: '/actions', icon: EditIcon, label: 'Правки', permission: 'view_analytics' },
  { to: '/journal', icon: ClockIcon, label: 'Журнал', permission: 'view_analytics' },
  { to: '/agency', icon: UsersIcon, label: 'Агентство' },
  { to: '/inbox', icon: InboxIcon, label: 'Входящие' },
  { to: '/chat', icon: ChatIcon, label: 'Чат', permission: 'use_chat' },
  { to: '/goals', icon: TargetIcon, label: 'Цели' },
  { to: '/projects', icon: FolderIcon, label: 'Проекты' },
  { to: '/digest', icon: DigestIcon, label: 'Дайджесты', permission: 'manage_digests' },
  { to: '/settings', icon: SettingsIcon, label: 'Настройки' },
];

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const { logout } = useAuthStore();
  const { can } = usePermissions();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  // Filter nav items based on permissions
  const visibleItems = navItems.filter((item) => {
    if (!item.permission) return true;
    return can(item.permission);
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />

      {/* Menu panel */}
      <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-card border-r border-border shadow-lg animate-in">
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-2">
            <LogoIcon className="h-8 w-8 text-foreground" />
            <span className="text-xl font-semibold tracking-tight text-foreground">Mira</span>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label="Закрыть меню"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-4 flex-1 space-y-1 px-3">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      'h-4 w-4',
                      isActive ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Logout - Bottom */}
        <div className="border-t border-border p-4">
          <button
            onClick={handleLogout}
            className="w-full rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Выйти
          </button>
        </div>
      </div>
    </div>
  );
}
